import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismCheckbox, PrismIcon } from '@mylighthouse/prism-ember';

export type ProductAccessState =
  | 'checked'
  | 'readonly'
  | 'unavailable'
  | 'unchecked';

interface SettingsUsersTablesCellsCellProductAccessSignature {
  Args: {
    cellData: ProductAccessState;
    column?: {
      valuePath: string;
    };
    row: {
      isDisabled?: boolean;
      onProductChange?: (valuePath: string, checked: boolean) => void;
      originalValues?: Record<string, ProductAccessState>;
    };
  };
}

export default class CellProductAccess extends Component<SettingsUsersTablesCellsCellProductAccessSignature> {
  get displayData(): ProductAccessState {
    if (this.args.row.isDisabled) {
      const valuePath = this.args.column?.valuePath;
      const original = valuePath
        ? this.args.row.originalValues?.[valuePath]
        : undefined;

      if (original) {
        return original;
      }
    }

    return this.args.cellData;
  }

  get isCheckbox(): boolean {
    return this.displayData === 'checked' || this.displayData === 'unchecked';
  }

  get isChecked(): boolean {
    return this.displayData === 'checked';
  }

  get isReadonly(): boolean {
    return this.displayData === 'readonly';
  }

  @action
  handleChange(): void {
    const valuePath = this.args.column?.valuePath;
    if (valuePath) {
      this.args.row.onProductChange?.(valuePath, !this.isChecked);
    }
  }

  <template>
    <div>
      {{#if this.isCheckbox}}
        <PrismCheckbox
          @checked={{this.isChecked}}
          @disabled={{@row.isDisabled}}
          @onChange={{this.handleChange}}
          data-test-id="cell-product-access-checkbox"
        />
      {{else if this.isReadonly}}
        <PrismIcon
          @color="success"
          @name="checkmark"
          @size="small"
          data-test-id="cell-product-access-icon"
        />
      {{else}}
        <PrismIcon
          @color="critical"
          @name="cross"
          @size="small"
          data-test-id="cell-product-access-icon"
        />
      {{/if}}
    </div>
  </template>
}
